import React, { useMemo, useRef, useState, useEffect } from "react";
import {
    ControlLabel,
    FormControl,
    FormGroup,
    Button,
    Row,
    Col,
    Well,
    Glyphicon,
    HelpBlock,
} from "react-bootstrap";
import * as blockConstants from "../constants";
import { blockButtonsModel, blockButtonsType } from "../models";
import { quizGetByEvent } from "../../../../../api/cp";
import { useNotificationContext } from "../../../UI/Notifications";

const BlockButtonFields = ({ model, event, onChange, onRemove }) => {
    const [buttonModel, setButtonModel] = useState(blockButtonsModel);
    const [quizes, setQuizes] = useState([]);
    const { showNotification } = useNotificationContext();
    const inputLinkRef = useRef(null);

    useMemo(() => {
        setButtonModel(model || blockButtonsModel);
    }, [model]);

    useEffect(() => {
        getQuizes();
    }, []);

    useEffect(() => {
        if (
            isQuizButtonType() &&
            !buttonModel[blockConstants.BLOCK_BUTTON_QUIZ_KEY] &&
            quizes.length
        ) {
            handleQuizChange(quizes[0].quizKey);
        }
    }, [buttonModel[blockConstants.BLOCK_BUTTON_TYPE]]);

    function isQuizButtonType() {
        return (
            buttonModel[blockConstants.BLOCK_BUTTON_TYPE] ===
            blockConstants.BLOCK_BUTTON_TYPE_QUIZ
        );
    }

    function isCustomBlockType() {
        return (
            (buttonModel[blockConstants.BLOCK_BUTTON_TYPE] !==
                blockConstants.BLOCK_BUTTON_TYPE_CUSTOM &&
                buttonModel[blockConstants.BLOCK_BUTTON_LINK] &&
                !isQuizButtonType()) ||
            buttonModel[blockConstants.BLOCK_BUTTON_TYPE] ===
                blockConstants.BLOCK_BUTTON_TYPE_CUSTOM
        );
    }

    async function getQuizes() {
        try {
            const payload = await quizGetByEvent(event.eventKey);
            const filtredQuizes = payload?.filter(({ visible }) => visible);
            if (filtredQuizes?.length) {
                setQuizes(filtredQuizes);
            }
        } catch ({ message = null }) {
            if (message) showNotification({ message });
        }
    }

    function getBlockButtonTypes() {
        const buttonTypes = Object.entries(blockButtonsType);

        if (event.moduleQuiz.enabled && quizes?.length) {
            return buttonTypes;
        }

        return buttonTypes.filter(
            ([type]) => type !== blockConstants.BLOCK_BUTTON_TYPE_QUIZ
        );
    }

    function handleChange(key, value) {
        const updatedModel = {
            ...buttonModel,
            [key]: value,
        };
        onChange(updatedModel);
    }

    function validateUrl(str) {
        const pattern = new RegExp(
            "^(https?:\\/\\/)" +
                "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" +
                "((\\d{1,3}\\.){3}\\d{1,3}))" +
                "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" +
                "(\\?[;&a-z\\d%_.~+=-]*)?" +
                "(\\#[-a-z\\d_]*)?$",
            "i"
        );

        return pattern.test(str);
    }

    function linkValidation() {
        if (
            !inputLinkRef.current ||
            !buttonModel[blockConstants.BLOCK_BUTTON_LINK]?.length
        )
            return null;

        const isValid = validateUrl(
            buttonModel[blockConstants.BLOCK_BUTTON_LINK]
        );

        if (!isValid) {
            const message = `Неверный формат ссылки.
                Ссылка должна содержать протокол (https или http) и доменное имя, включая корневой домен через точку (.ru, .рф .com и т.д.).
                Наример: https://domain.ru
            `;
            inputLinkRef.current.setCustomValidity(message);
        } else {
            inputLinkRef.current.setCustomValidity('');
        }

        return !isValid ? "error" : "success";
    }

    function handleQuizChange(quizKey) {
        const updatedModel = {
            ...buttonModel,
            [blockConstants.BLOCK_BUTTON_QUIZ_KEY]: quizKey,
        };
        onChange(updatedModel);
    }

    return (
        buttonModel && (
            <Well>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: "12px",
                    }}
                >
                    <FormControl
                        value={
                            buttonModel[blockConstants.BLOCK_BUTTON_TYPE] ||
                            blockConstants.BLOCK_BUTTON_TYPE_CUSTOM
                        }
                        componentClass="select"
                        style={{ maxWidth: "max-content" }}
                        onChange={(e) =>
                            handleChange(
                                blockConstants.BLOCK_BUTTON_TYPE,
                                e.target.value
                            )
                        }
                    >
                        {getBlockButtonTypes().map(([key, value]) => (
                            <option key={key} value={key}>
                                {value}
                            </option>
                        ))}
                    </FormControl>
                    <Button bsStyle="danger" bsSize="xsmall" onClick={onRemove}>
                        <Glyphicon glyph="trash" bsSize="xsmall" />
                    </Button>
                </div>
                <Row>
                    <Col xs={6}>
                        <FormGroup
                            controlId={blockConstants.BLOCK_BUTTON_TITLE_RU}
                        >
                            <ControlLabel>Название кнопки [Ru]</ControlLabel>
                            <FormControl
                                value={
                                    buttonModel[
                                        blockConstants.BLOCK_BUTTON_TITLE_RU
                                    ]
                                }
                                onChange={(e) =>
                                    handleChange(
                                        blockConstants.BLOCK_BUTTON_TITLE_RU,
                                        e.target.value
                                    )
                                }
                            />
                        </FormGroup>
                    </Col>
                    <Col xs={6}>
                        <FormGroup
                            controlId={blockConstants.BLOCK_BUTTON_TITLE_EN}
                        >
                            <ControlLabel>Название кнопки [En]</ControlLabel>
                            <FormControl
                                value={
                                    buttonModel[
                                        blockConstants.BLOCK_BUTTON_TITLE_EN
                                    ]
                                }
                                onChange={(e) =>
                                    handleChange(
                                        blockConstants.BLOCK_BUTTON_TITLE_EN,
                                        e.target.value
                                    )
                                }
                            />
                        </FormGroup>
                    </Col>
                </Row>
                <Row>
                    <Col xs={6}>
                        <FormGroup
                            controlId={blockConstants.BLOCK_BUTTON_OPTIONS}
                        >
                            <ControlLabel>Тип кнопки</ControlLabel>
                            <FormControl
                                componentClass="select"
                                value={
                                    buttonModel[
                                        blockConstants.BLOCK_BUTTON_OPTIONS
                                    ].type
                                }
                                onChange={(e) =>
                                    handleChange(
                                        blockConstants.BLOCK_BUTTON_OPTIONS,
                                        {
                                            ...buttonModel[
                                                blockConstants
                                                    .BLOCK_BUTTON_OPTIONS
                                            ],
                                            type: e.target.value,
                                        }
                                    )
                                }
                            >
                                <option value="filled">Заполненная</option>
                                <option value="bordered">Только контур</option>
                            </FormControl>
                        </FormGroup>
                    </Col>
                    {isCustomBlockType() && (
                        <Col xs={6}>
                            <FormGroup
                                validationState={linkValidation()}
                                controlId={blockConstants.BLOCK_BUTTON_LINK}
                            >
                                <ControlLabel>Ссылка</ControlLabel>
                                <FormControl
                                    inputRef={(ref) =>
                                        (inputLinkRef.current = ref)
                                    }
                                    value={
                                        buttonModel[
                                            blockConstants.BLOCK_BUTTON_LINK
                                        ]
                                    }
                                    onChange={(e) =>
                                        handleChange(
                                            blockConstants.BLOCK_BUTTON_LINK,
                                            e.target.value
                                        )
                                    }
                                    disabled={
                                        buttonModel[
                                            blockConstants.BLOCK_BUTTON_TYPE
                                        ] !==
                                        blockConstants.BLOCK_BUTTON_TYPE_CUSTOM
                                    }
                                />
                                {
                                    (!!inputLinkRef.current && !inputLinkRef.current.validity.valid) &&
                                    <HelpBlock>
                                        <span className="h6">
                                            Ссылка должна содержать протокол (https или http) и доменное имя, включая корневой домен через точку (.ru, .рф .com и т.д.). <br />
                                            <strong>Наример: </strong> https://domain.ru
                                        </span>
                                    </HelpBlock>
                                }
                            </FormGroup>
                        </Col>
                    )}
                    {isQuizButtonType() && (
                        <Col xs={6}>
                            <FormGroup>
                                <ControlLabel>Список квизов</ControlLabel>
                                <FormControl
                                    componentClass="select"
                                    value={
                                        buttonModel[
                                            blockConstants.BLOCK_BUTTON_QUIZ_KEY
                                        ]
                                    }
                                    onChange={(e) =>
                                        handleQuizChange(e.target.value)
                                    }
                                >
                                    {quizes?.map(({ quizKey, titleRu }) => (
                                        <option key={quizKey} value={quizKey}>
                                            {titleRu}
                                        </option>
                                    ))}
                                </FormControl>
                            </FormGroup>
                        </Col>
                    )}
                </Row>
            </Well>
        )
    );
};

export default BlockButtonFields;
